"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Order, Chat } from "@apis/index";
import { useOrderEvents } from "@hooks/useOrderEvents";

import OrderedProductDetails from "../ordered-product-details";
import OrderShippingInfo from "../order-shipping-info";
import { StyleDiv } from "@commons/style";
import Typography from "@components/typography";
import Row from "@components/row";
import Table from "@components/table";
import OrderStepper from "../order-shipping-info/orderStepper";

import { ActionButton } from "../../style";
import { useModal } from "@context/ModalContext";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import Invoice from "../invoice";
import { Helper } from "@utils/index";
import { OrderDetailsSkeleton } from "./skeleton";
import Button from "@components/button";
import { useNavigate } from "react-router-dom";
import useConfig from "@hooks/useConfig";

const OrderDetails = ({ id: propId }: { id?: string }) => {
  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const { settings } = useGeneralSettings();
  const { id: urlId } = useParams();
  const id = propId || urlId;
  const [order, setOrder] = useState<any>({});
  const [customerHistory, setCustomerHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const { showModal, hideModal } = useModal();
  const isInitialLoad = useRef(true);
  const user = Helper.getUser();
  const isStore = user?.role === "store" || user?.role === "store-employee";

  const handleChatWithCustomer = async () => {
    try {
      await Chat.startOrderChat(Number(id), "customer");
      navigate("/store/chat");
    } catch (error: any) {
      console.error("Failed to start order chat", error);
    }
  };

  const handleImagePreview = (imageUrl: string) => {
    showModal(
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#000",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <img
          src={imageUrl}
          alt="Prescription Preview"
          style={{
            maxWidth: "100%",
            maxHeight: "85vh",
            objectFit: "contain",
          }}
        />
      </div>,
      { width: "auto", padding: "0" },
    );
  };

  // Background refresh - doesn't show loading state
  const refreshOrder = useCallback(async () => {
    if (!id) return;
    try {
      const res = await Order.find(id);
      setOrder(res);
      if (res?.status) setCurrentStatus(res.status);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  // Fetch history separately to not block main order load if possible, or together
  const fetchHistory = useCallback(async (orderId: string) => {
    try {
      const res = await Order.getCustomerOrderHistory(orderId);
      if (res?.data) {
        setCustomerHistory(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch customer history", err);
    }
  }, []);

  // Initial fetch - shows loading state only on first load
  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const res = await Order.find(id);
        setOrder(res);
        if (res?.status) setCurrentStatus(res.status);

        // Track viewing event if the order is still "Prescription Requested"
        if (res?.status === "Prescription Requested" && !res?.viewedAt) {
          await Order.trackEvent(id, "viewing");
          // Refresh order immediately to get the new acceptanceDeadline (2 mins)
          const updatedRes = await Order.find(id);
          setOrder(updatedRes);
          if (updatedRes?.status) setCurrentStatus(updatedRes.status);
        }

        // Fetch history after getting order (or concurrently if API allows, but we need order ID which we have)
        fetchHistory(id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        isInitialLoad.current = false;
      }
    };
    fetchOrder();
  }, [id, fetchHistory]);

  // Listen for real-time order events - refetch silently when this order is updated
  useOrderEvents({
    onDeliveryAssigned: (data) => {
      if (data.orderId && String(data.orderId) === String(id)) {
        console.log(
          "🚴 Delivery assigned for this order, refreshing silently...",
        );
        refreshOrder();
      }
    },
    onOrderUpdated: (data) => {
      if (data.orderId && String(data.orderId) === String(id)) {
        console.log("🔄 Order updated, refreshing silently...");
        refreshOrder();
      }
    },
    onStatusChange: (data) => {
      if (data.orderId && String(data.orderId) === String(id)) {
        console.log("📊 Status changed, refreshing silently...");
        refreshOrder();
      }
    },
    playSound: false,
    showToasts: false,
  });

  // Handler for status updates - refetch the full order silently
  const handleStatusUpdate = useCallback(
    async (newStatus: string) => {
      setCurrentStatus(newStatus);
      // Silently refetch the full order to get updated data (including deliveryMan)
      await refreshOrder();
    },
    [refreshOrder],
  );

  // Filter out current order from history to find "Last Order" logic if needed,
  // or just take the most recent one that is NOT this order.
  // Assuming history is sorted by date desc.
  const previousOrders = customerHistory.filter(
    (o) => String(o.id) !== String(id),
  );
  const lastOrder = previousOrders.length > 0 ? previousOrders[0] : null;

  // Filter for table
  const uniqueStatuses = Array.from(
    new Set(previousOrders.map((o) => o.status)),
  ).filter(Boolean);
  const filteredOrders =
    statusFilter === "All"
      ? previousOrders
      : previousOrders.filter((o) => o.status === statusFilter);

  if (loading) return <OrderDetailsSkeleton />;
  if (!order) return <div>Order not found</div>;

  return (
    <div>
      <Typography styleText="font-size:22px;font-weight:500">
        Order details #{order.id}
      </Typography>
      <OrderStepper
        currentStatus={currentStatus}
        orderId={order?.id}
        isPharma={order?.module?.type?.toLowerCase() === "pharma"}
      />
      <br />

      <Row>
        <div
          style={{
            flex: 3,
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <OrderedProductDetails
            singleOrderDetails={order || {}}
            refreshOrder={refreshOrder}
          />

          {previousOrders.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Typography styleText="font-size:18px;font-weight:600;color:#111827">
                  Customer Order History
                </Typography>
                <select
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                    cursor: "pointer",
                  }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All Statuses</option>
                  {uniqueStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <Table
                columns={[
                  {
                    title: "Order ID",
                    key: "id",
                    render: (item: any) => (
                      <span style={{ fontWeight: 600 }}>#{item.id}</span>
                    ),
                  },
                  {
                    title: "Tracking ID",
                    key: "tracking",
                    render: (item: any) => (
                      <span style={{ fontWeight: 600 }}>
                        #{item.trackingId}
                      </span>
                    ),
                  },
                  {
                    title: "Date",
                    key: "createdAt",
                    render: (item: any) =>
                      new Date(item.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                  },
                  {
                    title: "Items",
                    key: "items",
                    render: (item: any) => item.items?.length || 0,
                  },
                  {
                    title: "Amount",
                    key: "totalAmount",
                    render: (item: any) => (
                      <span style={{ fontWeight: 600 }}>
                        {Helper.formatCurrency(item.totalAmount, settings)}
                      </span>
                    ),
                  },
                  {
                    title: "Payment",
                    key: "paymentStatus",
                    render: (item: any) => (
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          background:
                            item.paymentStatus === "Paid"
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            item.paymentStatus === "Paid"
                              ? "#065f46"
                              : "#991b1b",
                        }}
                      >
                        {item.paymentStatus || "Unpaid"}
                      </span>
                    ),
                  },
                  {
                    title: "Status",
                    key: "status",
                    render: (item: any) => {
                      let backgroundColor = "#f3f4f6";
                      let textColor = "#374151";
                      const statusLower = item.status?.toLowerCase() || "";

                      if (
                        ["delivered", "completed", "done", "success"].some(
                          (s) => statusLower.includes(s),
                        )
                      ) {
                        backgroundColor = "#d1fae5"; // green-100
                        textColor = "#065f46"; // green-800
                      } else if (
                        ["cancelled", "rejected", "failed"].some((s) =>
                          statusLower.includes(s),
                        )
                      ) {
                        backgroundColor = "#fee2e2"; // red-100
                        textColor = "#991b1b"; // red-800
                      } else if (
                        ["pending", "processing"].some((s) =>
                          statusLower.includes(s),
                        )
                      ) {
                        backgroundColor = "#dbeafe"; // blue-100
                        textColor = "#1e40af"; // blue-800
                      } else if (
                        ["shipped", "dispatched", "out for delivery"].some(
                          (s) => statusLower.includes(s),
                        )
                      ) {
                        backgroundColor = "#ffedd5"; // orange-100
                        textColor = "#9a3412"; // orange-800
                      } else if (
                        ["confirmed", "approved"].some((s) =>
                          statusLower.includes(s),
                        )
                      ) {
                        backgroundColor = "#dcfce7"; // green-100 variant
                        textColor = "#166534"; // green-800 variant
                      } else if (statusLower.includes("prescription")) {
                        backgroundColor = "#e0f2f1"; // teal-100
                        textColor = "#00695c"; // teal-800
                      } else if (statusLower.includes("creating invoice")) {
                        backgroundColor = "#fce7f3"; // pink-100
                        textColor = "#9d174d"; // pink-800
                      } else if (statusLower.includes("invoice")) {
                        backgroundColor = "#f3e5f5"; // purple-100
                        textColor = "#6a1b9a"; // purple-800
                      } else if (statusLower.includes("viewing")) {
                        backgroundColor = "#fef3c7"; // yellow-100
                        textColor = "#92400e"; // yellow-800
                      }

                      return (
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            background: backgroundColor,
                            color: textColor,
                            fontWeight: 500,
                          }}
                        >
                          {item.status}
                        </span>
                      );
                    },
                  },
                  {
                    title: "Action",
                    key: "action",
                    render: (item: any) => (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <ActionButton
                          variant="view"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/admin/orders/details/${item.id}`;
                          }}
                          title="View Order"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </ActionButton>
                        <ActionButton
                          variant="print"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const fullOrder = await Order.find(item.id);
                              showModal(<Invoice order={fullOrder} />, {
                                width: "480px",
                              });
                            } catch (err) {
                              console.error(
                                "Failed to load order for invoice",
                                err,
                              );
                            }
                          }}
                          title="View Receipt"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
                            <line x1="8" y1="8" x2="16" y2="8" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                            <line x1="8" y1="16" x2="12" y2="16" />
                          </svg>
                        </ActionButton>
                      </div>
                    ),
                  },
                ]}
                data={filteredOrders}
                hasSearchbar={false}
                hasExportButton={false}
              />
            </>
          )}
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {order?.prescriptionFiles && order.prescriptionFiles.length > 0 && (
            <div
              style={{
                width: "100%",
                background: "#fff",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography styleText="font-size:14px;font-weight:600;margin-bottom:10px;color:#111827">
                Prescription ({order.prescriptionType})
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {order.prescriptionFiles.map((file: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      overflow: "hidden",
                      width: "100%",
                      height: "80px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleImagePreview(`${bucketUrl}/${file}`)}
                  >
                    <img
                      src={`${bucketUrl}/${file}`}
                      alt="Prescription"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {order?.notes && (
            <div
              style={{
                width: "100%",
                background: "#fff",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography styleText="font-size:12px;font-weight:600;margin-bottom:5px">
                Customer Notes:
              </Typography>
              <Typography styleText="font-size:13px">
                {order.notes}
              </Typography>
            </div>
          )}
          {isStore && (
            <Button
              title="Chat with Customer"
              onClick={handleChatWithCustomer}
            />
          )}
          <OrderShippingInfo
            singleOrderDetails={order || {}}
            onStatusUpdate={handleStatusUpdate}
            lastOrder={lastOrder}
          />
        </div>
      </Row>
    </div>
  );
};

export default OrderDetails;
